export const TOTAL_TIME_WITH_LAPS_TIMING_MODEL = 'total-time-with-laps'
export const CLASS_RELATIVE_TIMING_VERSION = 1

const MILLISECONDS_PER_SECOND = 1000

export function penaltyMillisecondsToWholeSeconds(penaltyTime) {
  const milliseconds = Number(penaltyTime ?? 0)

  return Number.isFinite(milliseconds)
    ? Math.round(milliseconds / MILLISECONDS_PER_SECOND)
    : 0
}

export function wholePenaltySecondsToMilliseconds(seconds) {
  const wholeSeconds = Number(seconds)
  const milliseconds = wholeSeconds * MILLISECONDS_PER_SECOND

  return Number.isInteger(wholeSeconds) && Number.isSafeInteger(milliseconds)
    ? milliseconds
    : 0
}

export function formatPenaltyAdjustment(penaltyTime) {
  const seconds = penaltyMillisecondsToWholeSeconds(penaltyTime)

  if (seconds === 0) return null

  return seconds > 0
    ? `+${seconds}s penalty`
    : `−${Math.abs(seconds)}s adjustment`
}

export function usesTotalTimeWithLaps(result) {
  return result?.metadata?.timingModel === TOTAL_TIME_WITH_LAPS_TIMING_MODEL
}

export function getResultAbsoluteTime(result) {
  const finishTime = Number(
    result?.metadata?.finishTime ?? result?.metadata?.totalTime,
  )

  return Number.isFinite(finishTime) && finishTime >= 0 ? finishTime : null
}

const getImportedFinishTime = getResultAbsoluteTime

function getEffectiveFinishTime(result) {
  const finishTime = getImportedFinishTime(result)
  const penaltyTime = Number(result?.penaltyTime ?? 0)

  if (finishTime == null) return null

  return finishTime + (Number.isFinite(penaltyTime) ? penaltyTime : 0)
}

function getClassTimingGroupKey(result, classId) {
  const splitId = result?.metadata?.splitId ?? 'no-split'

  return JSON.stringify([splitId, classId])
}

export function applyClassRelativeTotalTimes(results, classIdByResultOrUserId) {
  const classLeaders = new Map()

  for (const result of results) {
    if (
      result?.deleted ||
      result?.status !== 'finished' ||
      !usesTotalTimeWithLaps(result)
    ) {
      continue
    }

    const userId = result.userId || result.user?.id
    const classId =
      classIdByResultOrUserId.get(result.id) ||
      classIdByResultOrUserId.get(userId)

    if (!classId || getImportedFinishTime(result) == null) continue

    const groupKey = getClassTimingGroupKey(result, classId)

    const currentLeader = classLeaders.get(groupKey)
    const resultLaps = Number(result.laps)
    const currentLeaderLaps = Number(currentLeader?.laps)
    const hasMoreLaps =
      Number.isFinite(resultLaps) &&
      (!Number.isFinite(currentLeaderLaps) || resultLaps > currentLeaderLaps)
    const isFasterOnSameLap =
      Number.isFinite(resultLaps) &&
      resultLaps === currentLeaderLaps &&
      getEffectiveFinishTime(result) < getEffectiveFinishTime(currentLeader)

    if (!currentLeader || hasMoreLaps || isFasterOnSameLap) {
      classLeaders.set(groupKey, result)
    }
  }

  return results.map((result) => {
    if (
      result?.deleted ||
      result?.status !== 'finished' ||
      !usesTotalTimeWithLaps(result)
    ) {
      return result
    }

    const userId = result.userId || result.user?.id
    const classId =
      classIdByResultOrUserId.get(result.id) ||
      classIdByResultOrUserId.get(userId)
    const classLeader = classLeaders.get(
      getClassTimingGroupKey(result, classId),
    )
    const finishTime = getImportedFinishTime(result)
    const leaderFinishTime = getImportedFinishTime(classLeader)

    if (
      !classId ||
      !classLeader ||
      finishTime == null ||
      leaderFinishTime == null
    ) {
      return result
    }

    const isClassLeader = result === classLeader
    const effectiveFinishTime = getEffectiveFinishTime(result)
    const leaderEffectiveFinishTime = getEffectiveFinishTime(classLeader)
    const resultLaps = Number(result.laps)
    const leaderLaps = Number(classLeader.laps)
    const lapDifference = leaderLaps - resultLaps
    const isLapped =
      !isClassLeader &&
      Number.isFinite(resultLaps) &&
      Number.isFinite(leaderLaps) &&
      lapDifference > 0

    return {
      ...result,
      type: isClassLeader ? 'TIME' : isLapped ? 'LAP' : 'GAP',
      value: isClassLeader
        ? finishTime
        : isLapped
          ? lapDifference
          : Math.max(0, effectiveFinishTime - leaderEffectiveFinishTime),
      metadata: {
        ...(result.metadata || {}),
        classLeaderFinishTime: leaderFinishTime,
        classLeaderEffectiveFinishTime: leaderEffectiveFinishTime,
        classRelativeTimingVersion: CLASS_RELATIVE_TIMING_VERSION,
      },
    }
  })
}

export function synchronizeTotalTimeResultEdit(result, changes = {}) {
  if (
    !usesTotalTimeWithLaps(result) ||
    !Object.hasOwn(changes, 'value')
  ) {
    return result
  }

  const value = Number(result.value)
  const penaltyTime = Number(result.penaltyTime ?? 0)
  let finishTime = null

  if (result.type === 'TIME') {
    finishTime = value
  } else if (result.type === 'GAP') {
    const classLeaderEffectiveFinishTime = Number(
      result.metadata?.classLeaderEffectiveFinishTime,
    )

    if (Number.isFinite(classLeaderEffectiveFinishTime)) {
      finishTime =
        classLeaderEffectiveFinishTime +
        value -
        (Number.isFinite(penaltyTime) ? penaltyTime : 0)
    }
  }

  if (!Number.isFinite(finishTime) || finishTime < 0) return result

  return {
    ...result,
    metadata: {
      ...(result.metadata || {}),
      finishTime,
    },
  }
}

export function applyTrackedClassRelativeTotalTimes(
  results,
  classIdByResultOrUserId,
) {
  const rebasedResults = applyClassRelativeTotalTimes(
    results,
    classIdByResultOrUserId,
  )

  return rebasedResults.map((result, index) => {
    const previousResult = results[index]
    const timingChanged =
      result.type !== previousResult.type ||
      result.value !== previousResult.value ||
      result.metadata?.classLeaderFinishTime !==
        previousResult.metadata?.classLeaderFinishTime ||
      result.metadata?.classLeaderEffectiveFinishTime !==
        previousResult.metadata?.classLeaderEffectiveFinishTime ||
      result.metadata?.classRelativeTimingVersion !==
        previousResult.metadata?.classRelativeTimingVersion

    return timingChanged && !result.created && !result.deleted
      ? { ...result, updated: true }
      : result
  })
}
